/**
 * Singleton imperative action refs that Viewport3D sets each render.
 * App.tsx (toolbar / menu) calls these to trigger actions that depend
 * on Viewport3D's internal WebGL state.
 */
export const viewportActions = {
  undo:           { current: (() => {}) as () => void },
  redo:           { current: (() => {}) as () => void },
  save:           { current: (async () => {}) as () => Promise<void> },
  open:           { current: (async () => {}) as () => unknown },
  /** Load a project directly from a File object (used by autosave recovery). */
  loadFile:       { current: (async (_file: File, _handle?: any) => {}) as (file: File, handle?: any) => Promise<void> },
  /**
   * Called by Viewport3D after the user picks a file (Open dialog / ProjectModal).
   * App.tsx sets this to compare the file name against any autosave and show
   * the recovery modal if there is a name match; otherwise loads directly.
   */
  loadFileWithAutosaveCheck: { current: (async (_file: File, _handle?: any) => {}) as (file: File, handle?: any) => Promise<void> },
  fill:           { current: (() => {}) as () => void },
  clearLayer:     { current: (() => {}) as () => void },
  deleteSelectedLayer: { current: (() => {}) as () => void },
  /** Tracks the number of selected timeline keyframes; used to guard layer-delete hotkey. */
  selectedKfsCount: { current: 0 },
  newProject:     { current: (() => {}) as () => void },
  snapToHero:     { current: ((_id: string) => {}) as (id: string) => void },
  resetCamera:    { current: (() => {}) as () => void },
  /** Call before ANY undoable mutation to snapshot current world state. */
  pushSnapshot:   { current: (() => {}) as () => void },
  /** Deprecated alias kept for LayersPanel — same as pushSnapshot. */
  pushLayerSnapshot: { current: (() => {}) as () => void },
  /** UV-mode draw: start a new stroke at the given UV [0,1] coordinates. */
  uvPaintStart: { current: ((_u: number, _v: number, _pressure: number) => {}) as (u: number, v: number, pressure: number) => void },
  /** UV-mode draw: continue an in-progress stroke to a new UV [0,1] position. */
  uvPaintMove:  { current: ((_u: number, _v: number, _pressure: number) => {}) as (u: number, v: number, pressure: number) => void },
  /** UV-mode draw: commit and finish the current stroke. */
  uvPaintEnd:   { current: (() => {}) as () => void },
  /** UV-mode stamp: apply stamp at UV coordinate on click. */
  uvStampApply: { current: ((_u: number, _v: number) => {}) as (u: number, v: number) => void },
  /** UV-mode stamp: update hover preview as pointer moves. */
  uvStampPreviewMove: { current: ((_u: number, _v: number) => {}) as (u: number, v: number) => void },
  /** UV-mode text: apply text stamp at UV coordinate on click. */
  uvTextApply: { current: ((_u: number, _v: number) => {}) as (u: number, v: number) => void },
  /** UV-mode text: update hover preview as pointer moves. */
  uvTextPreviewMove: { current: ((_u: number, _v: number) => {}) as (u: number, v: number) => void },
  /** UV-mode: clear stamp/text preview (e.g. on pointer leave). */
  uvClearStampPreview: { current: (() => {}) as () => void },
  /** UV-mode rotate: begin a shift-drag rotation of the selected layer. */
  uvRotateStart: { current: ((_clientX: number, _clientY: number) => {}) as (clientX: number, clientY: number) => void },
  /** UV-mode rotate: update rotation as pointer moves. */
  uvRotateMove:  { current: ((_clientX: number, _clientY: number) => {}) as (clientX: number, clientY: number) => void },
  /** UV-mode rotate: commit rotation at drag end. */
  uvRotateEnd:   { current: (() => {}) as () => void },
  /** UV-mode translate: begin a shift+alt-drag translation of the selected layer. */
  uvTranslateStart: { current: ((_clientX: number, _clientY: number) => {}) as (clientX: number, clientY: number) => void },
  /** UV-mode translate: update translation as pointer moves. */
  uvTranslateMove:  { current: ((_clientX: number, _clientY: number, _refWidth: number) => {}) as (clientX: number, clientY: number, refWidth: number) => void },
  /** UV-mode translate: commit translation at drag end. */
  uvTranslateEnd:   { current: (() => {}) as () => void },
  /** Read clipboard image, compute fit-to-layer scale, set paste state. */
  pasteClipboard:       { current: (async () => {}) as () => Promise<void> },
  /** Bake the pending clipboard paste into the active layer RT at current scale. */
  commitClipboardPaste: { current: (() => {}) as () => void },
  /** Cancel a pending clipboard paste without committing. */
  cancelClipboardPaste: { current: (() => {}) as () => void },
  /**
   * Live stroke preview canvas for UV mode. Viewport3D writes the
   * in-progress stroke render-target pixels here (Y-flipped, rAF-throttled)
   * so UVPanel can overlay them during drawing without a React re-render.
   * null until the first UV stroke begins.
   */
  strokeLiveCanvas: { current: null as HTMLCanvasElement | null },
  /**
   * Called by Viewport3D after each rAF that writes new content into
   * strokeLiveCanvas (paint strokes) and after clearStampPreview.
   * UVPanel sets this to mark its CanvasTexture dirty and call invalidate().
   */
  notifyUvStrokeLive: { current: (() => {}) as () => void },
};
